import React, { useEffect, useMemo } from "react"
import { cva } from "class-variance-authority"
import cn from "../../../utils/classes"

export type BackgroundColor =
  | "white"
  | "secondaryGray"
  | "lightGray"
  | "transparent"
export type Justify = "center" | "start" | "end" | "between" | "none"
export type ButtonWrapperStyleType = "table"

export interface ButtonWrapperProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean
  children: React.ReactNode
  onClick?: () => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  width?: "full" | "fit"
  backgroundColor?: BackgroundColor
  justification?: Justify
  styleOverrides?: string
  childStyleOverrides?: string
  shouldWrapChildren?: boolean
  ariaLabel?: string
  ariaDescription?: string
  id?: string
  tabIndex?: number
  onFocus?: () => void
  forceFocus?: boolean
  buttonWrapperStyleType?: ButtonWrapperStyleType
  hidden?: boolean
}

/**
 * `ButtonWrapper` is a customizable button wrapper component.
 *
 * @component
 * @example
 * // Example usage of ButtonWrapper
 * <ButtonWrapper width="fit" backgroundColor="lightGray" onClick={() => console.log("Button wrapper clicked!")}>
 *   <p>Button content</p>
 * </ButtonWrapper>
 *
 * @param {object} props - The properties that define the ButtonWrapper component.
 * @param {boolean} [props.disabled=false] - If true, the button will be disabled.
 * @param {React.ReactNode} props.children - The content of the button.
 * @param {function} props.onClick - The function to call when the button is clicked.
 * @param {function} props.onKeyDown - The function to call when a key is pressed.
 * @param {("full"|"fit")} [props.width='full'] - The width of the button.
 * @param {("white"|"secondaryGray"|"lightGray"|"transparent")} [props.backgroundColor='white'] - The background color of the button.
 * @param {("center"|"start"|"end"|"between"|"none")} [props.justification='center'] - The justification of the button content.
 * @param {string} props.styleOverrides - Override styles for the button.
 * @param {boolean} [props.shouldWrapChildren=true] - If false, will not wrap children in an extra div.
 * @param {string} props.childStyleOverrides - Override styles for the extra div that wraps the children.
 * @param {string} [props.ariaLabel] - The aria-label attribute for the button for accessibility.
 * @param {string} [props.ariaDescription='button'] - The aria-description attribute for the button for accessibility.
 * @param {string} props.id - The id of the button.
 * @param {number} [props.tabIndex=0] - The tab index of the button.
 * @param {function} props.onFocus - The function to call when the button is focused.
 * @param {boolean} [props.forceFocus=false] - If true, the button will be focused when it is rendered.
 * @param {("table")} [props.buttonWrapperStyleType='table'] - The style type of the button wrapper.
 * @param {boolean} [props.hidden=false] - If true, the button will be hidden.
 *
 * @returns {React.Component} The ButtonWrapper component.
 */
export const ButtonWrapper = React.forwardRef<
  HTMLButtonElement,
  ButtonWrapperProps
>(
  (
    {
      disabled = false,
      children,
      onClick,
      width = "fit",
      onKeyDown,
      backgroundColor = "white",
      justification = "center",
      styleOverrides,
      shouldWrapChildren = true,
      childStyleOverrides,
      ariaLabel,
      ariaDescription,
      id,
      tabIndex = 0,
      onFocus,
      forceFocus = false,
      buttonWrapperStyleType,
      hidden = false,
      ...rest
    },
    ref,
  ) => {
    const justificationStyle = `justify-${justification}`
    const disabledStyle = "opacity-80 cursor-not-allowed"
    // TODO (NF): hoist these to other components as needed (button/button wrapper both come to mind first)
    //       will do this in my next PR when I start integrating with affiliate-web
    const focusAndActiveStyle =
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2198CC] focus-visible:ring-offset-2 focus-visible:ring-offset-white active:outline-none active:ring-2 active:ring-[#2198CC] active:ring-offset-2 active:ring-offset-white"

    const buttonVariants = cva(
      [
        "cursor-pointer",
        "flex",
        "justify-bottom",
        "cursor-pointer",
        "border-0",
        "rounded-md",
        "font-display",
        "text-4",
      ],
      {
        variants: {
          backgroundColor: {
            white: `bg-white ${focusAndActiveStyle}`,
            secondaryGray: "bg-secondary-grey",
            lightGray: "bg-light-grey",
            transparent: `bg-transparent ${focusAndActiveStyle}`,
            tertiaryGray: "bg-tertiary-grey",
          },
          width: {
            full: "w-full",
            fit: "w-fit",
          },
          disabled: {
            true: disabledStyle,
          },
          hidden: {
            true: "hidden",
          },
          buttonWrapperStyleType: {
            table: "p-0 font-default",
          },
        },
      },
    )

    const childVariants = cva([], {
      variants: {
        disabled: {
          true: disabledStyle,
        },
      },
    })

    const buttonClassName = useMemo(() => {
      return cn(
        "minimum-touchtarget",
        buttonVariants({
          backgroundColor,
          width,
          disabled,
          hidden,
          buttonWrapperStyleType,
        }),
        justificationStyle,
        styleOverrides,
      )
    }, [
      backgroundColor,
      width,
      disabled,
      hidden,
      justificationStyle,
      styleOverrides,
      buttonVariants,
      buttonWrapperStyleType,
    ])

    const childClassName = useMemo(() => {
      return cn(
        childVariants({ disabled }),
        justificationStyle,
        childStyleOverrides,
      )
    }, [disabled, justificationStyle, childStyleOverrides, childVariants])

    const buttonId = useMemo(() => {
      return id || crypto.randomUUID()
    }, [id])

    useEffect(() => {
      if (forceFocus) {
        const button = document.getElementById(buttonId)
        button?.focus()
      }
    }, [forceFocus, buttonId])

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={buttonClassName}
        id={buttonId}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        aria-description={ariaDescription}
        tabIndex={tabIndex}
        hidden={hidden}
        onFocus={onFocus}
        {...rest}
      >
        {shouldWrapChildren ? (
          <div className={childClassName}>{children}</div>
        ) : (
          children
        )}
      </button>
    )
  },
)
ButtonWrapper.displayName = "ButtonWrapper"
